import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Header from "../../components/Header";
import api from "../../utils/api";
import { getImageUrl } from "../../utils/imageUtils";
import {
  toLocalizedNumber,
  toLocalizedPrice,
  toLocalizedStatus,
} from "../../utils/localeUtils";
import { usePopup } from "../../context/PopupContext";
import { FaClipboard, FaTimes, FaSearch, FaBox, FaTruck } from "react-icons/fa";
import Pagination from "../../components/Pagination";
import "./FarmerOrderManagement.css";

const PER_PAGE = 10;

const FarmerOrderManagement = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showToast, showConfirm } = usePopup();
  const [, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orders, setOrders] = useState([]);
  const [page, setPage] = useState(1);

  useEffect(() => {
    const userData = localStorage.getItem("user");
    if (userData) {
      try {
        const userObj = JSON.parse(userData);
        setUser(userObj);
        if (userObj.role !== "farmer") {
          navigate("/");
          return;
        }
        fetchOrders();
      } catch (error) {
        console.error("Error parsing user data:", error);
        setLoading(false);
      }
    } else {
      navigate("/login");
    }
  }, [navigate]);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const response = await api.get("/orders");
      const ordersData = response.data || [];

      // Transform orders to match frontend format
      const transformedOrders = ordersData.map((order) => ({
        _id: order._id,
        orderNumber: order._id.slice(-10).toUpperCase(),
        customer: {
          _id: order.consumer?._id || order.consumer,
          username: order.consumer?.username || null,
          name: order.consumer?.name || "Customer",
          phone: order.consumer?.phone || "",
          email: order.consumer?.email || "",
          profileImage: order.consumer?.profileImage || null,
        },
        items: order.items.map((item) => ({
          product: item.product?._id || item.product,
          productName: item.name || item.product?.name || "Product",
          name: item.name || item.product?.name || "Product",
          quantity: item.quantity,
          price: item.price,
          unit: item.product?.unit || "kg",
          image: item.product?.images?.[0]
            ? getImageUrl(item.product.images[0])
            : null,
        })),
        total: order.totalPrice || 0,
        status: order.orderStatus || "pending",
        paymentMethod: order.paymentMethod?.toLowerCase() || "cod",
        paymentStatus: order.paymentStatus || "pending",
        delivery: {
          method: order.shippingAddress ? "home" : "pickup",
          address: order.shippingAddress?.street || "Farm Pickup",
        },
        createdAt: order.createdAt,
        deliveredAt: order.deliveredAt,
        notes: "",
      }));

      setOrders(transformedOrders);
    } catch (error) {
      console.error("Error fetching orders:", error);
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  const [showModal, setShowModal] = useState(false);

  const updateOrderStatus = async (orderId, newStatus) => {
    showConfirm({
      title: t("farmerOrderManagement.updateOrderStatus"),
      message: t("farmerOrderManagement.updateStatusMessage", {
        status: toLocalizedStatus(newStatus, t),
      }),
      confirmLabel: t("farmerOrderManagement.updateLabel"),
      onConfirm: async () => {
        try {
          await api.put(`/orders/${orderId}/status`, {
            orderStatus: newStatus,
          });
          await fetchOrders();
          showToast(
            t("farmerOrderManagement.statusUpdated", {
              status: toLocalizedStatus(newStatus, t),
            }),
            "success",
          );
        } catch (error) {
          console.error("Error updating order status:", error);
          showToast(t("farmerOrderManagement.updateStatusFailed"), "error");
        }
      },
    });
  };

  const openOrderDetails = (order) => {
    setSelectedOrder(order);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedOrder(null);
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: "#FF9800",
      confirmed: "#2196F3",
      processing: "#9C27B0",
      shipped: "#00BCD4",
      delivered: "#4CAF50",
      received: "#0d9488",
      cancelled: "#F44336",
    };
    return colors[status] || "#666";
  };

  useEffect(() => {
    setPage(1);
  }, [filterStatus, searchQuery]);

  const filteredOrders = orders.filter((order) => {
    const matchesStatus =
      filterStatus === "all" || order.status === filterStatus;
    const matchesSearch =
      !searchQuery.trim() ||
      order.orderNumber.toLowerCase().includes(searchQuery.toLowerCase()) ||
      order.customer.name.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  const totalPages = Math.ceil(filteredOrders.length / PER_PAGE);
  const pageOrders = filteredOrders.slice(
    (page - 1) * PER_PAGE,
    page * PER_PAGE,
  );

  const statusCounts = orders.reduce((acc, o) => {
    acc[o.status] = (acc[o.status] || 0) + 1;
    return acc;
  }, {});
  const statusOptions = [
    { value: "all", label: t("farmerOrderManagement.all") },
    { value: "pending", label: t("farmerOrderManagement.pending") },
    { value: "confirmed", label: t("farmerOrderManagement.confirmed") },
    { value: "processing", label: t("farmerOrderManagement.processing") },
    { value: "shipped", label: t("farmerOrderManagement.shipped") },
    { value: "delivered", label: t("farmerOrderManagement.delivered") },
    { value: "received", label: toLocalizedStatus("received", t) },
  ];

  const handleStatusChange = async (orderId, newStatus) => {
    try {
      await api.put(`/orders/${orderId}/status`, { orderStatus: newStatus });
      await fetchOrders(); // Refresh orders
      if (selectedOrder && selectedOrder._id === orderId) {
        setSelectedOrder({ ...selectedOrder, status: newStatus });
      }
    } catch (error) {
      console.error("Error updating order status:", error);
      showToast(t("farmerOrderManagement.updateStatusFailed"), "error");
    }
  };

  const handleMarkCODPaid = async (orderId) => {
    try {
      await api.put(`/payments/cod/confirm/order/${orderId}`);
      await fetchOrders();
      if (selectedOrder && selectedOrder._id === orderId) {
        setSelectedOrder({ ...selectedOrder, paymentStatus: "paid" });
      }
      showToast(t("farmerOrderManagement.codMarkedPaid"), "success");
    } catch (error) {
      const msg =
        error.response?.data?.message ||
        t("farmerOrderManagement.markPaidFailed");
      showToast(msg, "error");
    }
  };

  if (loading) {
    return (
      <div className="farmer-order-management-page">
        <Header />
        <div className="farmer-order-management-loading-state">
          <div className="farmer-order-management-spinner"></div>
          <p>{t("farmerOrderManagement.loading")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="farmer-order-management-page">
      <Header />

      <div className="farmer-order-management-container-modern">
        <header className="farmer-order-management-header">
          <div className="farmer-order-management-header-top">
            <h1 className="farmer-order-management-title">
              {t("farmerOrderManagement.title")}
            </h1>
            <p className="farmer-order-management-subtitle">
              {t("farmerOrderManagement.subtitle")}
            </p>
          </div>
          <div className="farmer-order-management-toolbar">
            <div className="farmer-order-management-search-wrap">
              <FaSearch className="farmer-order-management-search-icon" />
              <input
                type="text"
                placeholder={t("farmerOrderManagement.searchPlaceholder")}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="farmer-order-management-search"
              />
            </div>
            <div className="farmer-order-management-filters">
              {statusOptions.map(({ value, label }) => (
                <button
                  key={value}
                  type="button"
                  className={`farmer-order-management-filter-pill ${filterStatus === value ? "active" : ""}`}
                  onClick={() => setFilterStatus(value)}
                >
                  {label}
                  {value !== "all" && statusCounts[value] != null && (
                    <span className="farmer-order-management-filter-count">
                      {toLocalizedNumber(statusCounts[value])}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>
          <div className="farmer-order-management-stats">
            <span className="farmer-order-management-stats-text">
              {t("farmerOrderManagement.showing", {
                filtered: toLocalizedNumber(filteredOrders.length),
                total: toLocalizedNumber(orders.length),
              })}
            </span>
          </div>
        </header>

        {/* Orders Table */}
        <div className="farmer-order-management-orders-table-container">
          <table className="farmer-order-management-orders-table">
            <thead>
              <tr>
                <th>{t("farmerOrderManagement.orderId")}</th>
                <th>{t("farmerOrderManagement.customerName")}</th>
                <th>{t("farmerOrderManagement.items")}</th>
                <th>{t("farmerOrderManagement.payment")}</th>
                <th>{t("farmerOrderManagement.total")}</th>
                <th>{t("farmerOrderManagement.deliveryType")}</th>
                <th>{t("farmerOrderManagement.status")}</th>
                <th>{t("farmerOrderManagement.action")}</th>
              </tr>
            </thead>
            <tbody>
              {filteredOrders.length === 0 ? (
                <tr>
                  <td
                    colSpan="8"
                    className="farmer-order-management-empty-table-cell"
                  >
                    <div className="farmer-order-management-empty-state">
                      <FaClipboard
                        className="farmer-order-management-empty-icon"
                        style={{ fontSize: "48px" }}
                      />
                      <p>{t("farmerOrderManagement.noOrdersFound")}</p>
                    </div>
                  </td>
                </tr>
              ) : (
                pageOrders.map((order) => (
                  <tr key={order._id}>
                    <td>{toLocalizedNumber(order.orderNumber)}</td>
                    <td>
                      <Link
                        to={`/users/${order.customer.username || order.customer._id}`}
                        className="farmer-order-management-customer-link"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {order.customer.name}
                      </Link>
                    </td>
                    <td>
                      {order.items.map((item, idx) => (
                        <span key={idx}>
                          {item.productName || item.name} (
                          {toLocalizedNumber(item.quantity)} {item.unit})
                          {idx < order.items.length - 1 && ", "}
                        </span>
                      ))}
                    </td>
                    <td>
                      <span className="farmer-order-management-payment-cell">
                        <span className="farmer-order-management-payment-method">
                          {order.paymentMethod === "cod"
                            ? t("farmerOrderManagement.cod")
                            : t("farmerOrderManagement.online")}
                        </span>
                        <span
                          className={`farmer-order-management-payment-status ${order.paymentStatus === "paid" ? "paid" : "unpaid"}`}
                        >
                          {order.paymentStatus === "paid"
                            ? t("farmerOrderManagement.paid")
                            : t("farmerOrderManagement.unpaid")}
                        </span>
                        {order.paymentMethod === "cod" &&
                          order.paymentStatus !== "paid" && (
                            <button
                              type="button"
                              className="farmer-order-management-btn-mark-paid"
                              onClick={async (e) => {
                                e.stopPropagation();
                                await handleMarkCODPaid(order._id);
                              }}
                            >
                              {t("farmerOrderManagement.markPaid")}
                            </button>
                          )}
                      </span>
                    </td>
                    <td>₨ {toLocalizedPrice(order.total)}</td>
                    <td>
                      {order.delivery.method === "home"
                        ? t("farmerOrderManagement.homeDelivery")
                        : t("farmerOrderManagement.pickup")}
                    </td>
                    <td>
                      {order.status === "received" ||
                      order.status === "cancelled" ? (
                        <span
                          className="farmer-order-management-status-badge"
                          style={{
                            background: getStatusColor(order.status),
                            color: "#fff",
                          }}
                        >
                          {toLocalizedStatus(order.status, t)}
                        </span>
                      ) : (
                        <select
                          value={order.status}
                          onChange={(e) =>
                            handleStatusChange(order._id, e.target.value)
                          }
                          className="farmer-order-management-status-select"
                          style={{
                            "--status-color": getStatusColor(order.status),
                          }}
                          title={t("farmerOrderManagement.changeStatus")}
                        >
                          <option value="pending">Pending</option>
                          <option value="confirmed">Confirmed</option>
                          <option value="processing">Processing</option>
                          <option value="shipped">Shipped</option>
                          <option value="delivered">Delivered</option>
                        </select>
                      )}
                    </td>
                    <td>
                      <div className="farmer-order-management-row-actions">
                        <button
                          onClick={() => openOrderDetails(order)}
                          className="farmer-order-management-btn-view"
                        >
                          <FaBox /> {t("farmerOrderManagement.details")}
                        </button>
                        <Link
                          to={`/farmer/order/${order._id}`}
                          className="farmer-order-management-btn-track"
                        >
                          <FaTruck /> {t("farmerOrderManagement.track")}
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <Pagination
          page={page}
          totalPages={totalPages}
          onPageChange={setPage}
        />

        {/* Order Details Modal */}
        {showModal && selectedOrder && (
          <div
            className="farmer-order-management-modal-overlay"
            onClick={closeModal}
          >
            <div
              className="farmer-order-management-modal-content"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="farmer-order-management-modal-header">
                <h2>{t("farmerOrderManagement.orderDetails")}</h2>
                <button
                  className="farmer-order-management-modal-close"
                  onClick={closeModal}
                >
                  <FaTimes />
                </button>
              </div>
              <div className="farmer-order-management-modal-body">
                <div className="farmer-order-management-modal-section">
                  <h3>{t("farmerOrderManagement.customerContact")}</h3>
                  <p>
                    <strong>{t("farmerOrderManagement.name")}:</strong>{" "}
                    <Link
                      to={`/users/${selectedOrder.customer.username || selectedOrder.customer._id}`}
                      className="farmer-order-management-customer-link"
                      onClick={closeModal}
                    >
                      {selectedOrder.customer.name}
                    </Link>
                  </p>
                  <p>
                    <strong>{t("farmerOrderManagement.phone")}:</strong>{" "}
                    {selectedOrder.customer.phone}
                  </p>
                  <p>
                    <strong>{t("farmerOrderManagement.email")}:</strong>{" "}
                    {selectedOrder.customer.email}
                  </p>
                </div>
                <div className="farmer-order-management-modal-section">
                  <h3>{t("farmerOrderManagement.items")}</h3>
                  <ul className="farmer-order-management-items-list">
                    {selectedOrder.items.map((item, index) => (
                      <li
                        key={index}
                        className="farmer-order-management-item-row"
                      >
                        {item.image && (
                          <img
                            src={item.image}
                            alt={item.productName || item.name}
                            className="farmer-order-management-item-image"
                            onError={(e) => {
                              e.target.style.display = "none";
                            }}
                          />
                        )}
                        <div className="farmer-order-management-item-info">
                          <span className="farmer-order-management-item-name">
                            {item.productName || item.name}
                          </span>
                          <span className="farmer-order-management-item-details">
                            {item.quantity} {item.unit} × ₨ {item.price} = ₨{" "}
                            {(item.quantity * item.price).toFixed(2)}
                          </span>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
                {selectedOrder.notes && (
                  <div className="farmer-order-management-modal-section">
                    <h3>{t("farmerOrderManagement.notes")}</h3>
                    <p>{selectedOrder.notes}</p>
                  </div>
                )}
                <div className="farmer-order-management-modal-section">
                  <h3>{t("farmerOrderManagement.orderSummary")}</h3>
                  <p>
                    <strong>{t("farmerOrderManagement.totalLabel")}:</strong> ₨{" "}
                    {toLocalizedPrice(selectedOrder.total)}
                  </p>
                  <p>
                    <strong>{t("farmerOrderManagement.delivery")}:</strong>{" "}
                    {selectedOrder.delivery.method === "home"
                      ? t("farmerOrderManagement.homeDelivery")
                      : t("farmerOrderManagement.pickup")}
                  </p>
                  <p>
                    <strong>{t("farmerOrderManagement.paymentLabel")}:</strong>{" "}
                    {selectedOrder.paymentMethod === "cod"
                      ? t("farmerOrderManagement.cashOnDelivery")
                      : t("farmerOrderManagement.online")}{" "}
                    –{" "}
                    {selectedOrder.paymentStatus === "paid"
                      ? t("farmerOrderManagement.paid")
                      : t("farmerOrderManagement.unpaid")}
                  </p>
                  {selectedOrder.paymentMethod === "cod" &&
                    selectedOrder.paymentStatus !== "paid" && (
                      <button
                        type="button"
                        className="farmer-order-management-btn-mark-paid-modal"
                        onClick={async () => {
                          await handleMarkCODPaid(selectedOrder._id);
                          closeModal();
                        }}
                      >
                        {t("farmerOrderManagement.markCodPaid")}
                      </button>
                    )}
                </div>
              </div>
              <div className="farmer-order-management-modal-footer">
                <button
                  onClick={closeModal}
                  className="farmer-order-management-btn-cancel"
                >
                  {t("farmerOrderManagement.close")}
                </button>
                <Link
                  to={`/farmer/order/${selectedOrder._id}`}
                  className="farmer-order-management-btn-track-order"
                  onClick={closeModal}
                >
                  <FaTruck /> {t("farmerOrderManagement.trackOrder")}
                </Link>
                {selectedOrder.status === "received" ||
                selectedOrder.status === "cancelled" ? (
                  <span
                    className="farmer-order-management-status-badge"
                    style={{
                      background: getStatusColor(selectedOrder.status),
                      color: "#fff",
                      padding: "8px 16px",
                      borderRadius: "8px",
                      fontWeight: 600,
                    }}
                  >
                    {toLocalizedStatus(selectedOrder.status, t)}
                  </span>
                ) : (
                  <>
                    <select
                      value={selectedOrder.status}
                      onChange={(e) =>
                        handleStatusChange(selectedOrder._id, e.target.value)
                      }
                      className="farmer-order-management-status-select-modal"
                    >
                      <option value="pending">
                        {toLocalizedStatus("pending", t)}
                      </option>
                      <option value="confirmed">
                        {toLocalizedStatus("confirmed", t)}
                      </option>
                      <option value="processing">
                        {toLocalizedStatus("processing", t)}
                      </option>
                      <option value="shipped">
                        {toLocalizedStatus("shipped", t)}
                      </option>
                      <option value="delivered">
                        {toLocalizedStatus("delivered", t)}
                      </option>
                    </select>
                    <button
                      onClick={async () => {
                        await updateOrderStatus(
                          selectedOrder._id,
                          selectedOrder.status,
                        );
                        closeModal();
                      }}
                      className="farmer-order-management-btn-update-status"
                    >
                      {t("farmerOrderManagement.updateStatus")}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FarmerOrderManagement;
